import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FaBars, FaTimes, FaChevronDown, FaHome, FaInfoCircle, FaImages, FaVideo, FaBox, FaBlog, FaFilePdf, FaQuestionCircle, FaEnvelope } from 'react-icons/fa';
import WhatsAppPopup from '../WhatsAppPopup';
import { motion, AnimatePresence } from 'framer-motion';

interface NavItem {
  label: string;
  path: string;
  children?: NavItem[];
}

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState<Record<string, boolean>>({});
  const location = useLocation();

  // Close all dropdowns when location changes
  useEffect(() => {
    setDropdownOpen({});
  }, [location]);
  const [whatsappPopupOpen, setWhatsappPopupOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const navItems: NavItem[] = [
    { label: 'Home', path: '/' },
    { label: 'About', path: '/about' },
    {
      label: 'Projects',
      path: '#projects',
      children: [
        { label: 'Project Photo', path: '/project-photos' },
        { label: 'Project Video', path: '/project-videos' },
      ],
    },
    { label: 'Packages', path: '/packages' },
    { label: 'Blog', path: '/events' },
    {
      label: 'Contact',
      path: '#contact',
      children: [
        { label: 'Brochures', path: '/brochures' },
        { label: 'Inquiry', path: '/inquiry' },
        { label: 'Contact Us', path: '/contact' },
      ],
    },
  ];

  // Mobile navigation items with larger colorful icons and matching dots
  const mobileNavItems = [
    { 
      label: 'Home', 
      path: '/', 
      icon: <FaHome className="w-7 h-7 text-blue-500 group-hover:text-blue-400 transition-colors" />,
      dotGradient: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
      dotShadow: 'rgba(59, 130, 246, 0.6)'
    },
    { 
      label: 'About', 
      path: '/about', 
      icon: <FaInfoCircle className="w-7 h-7 text-emerald-500 group-hover:text-emerald-400 transition-colors" />,
      dotGradient: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
      dotShadow: 'rgba(16, 185, 129, 0.6)'
    },
    { 
      label: 'Project Photos', 
      path: '/project-photos', 
      icon: <FaImages className="w-7 h-7 text-purple-500 group-hover:text-purple-400 transition-colors" />,
      dotGradient: 'linear-gradient(135deg, #a855f7 0%, #7e22ce 100%)',
      dotShadow: 'rgba(168, 85, 247, 0.6)'
    },
    { 
      label: 'Project Videos', 
      path: '/project-videos', 
      icon: <FaVideo className="w-7 h-7 text-rose-500 group-hover:text-rose-400 transition-colors" />,
      dotGradient: 'linear-gradient(135deg, #f43f5e 0%, #e11d48 100%)',
      dotShadow: 'rgba(244, 63, 94, 0.6)'
    },
    { 
      label: 'Packages', 
      path: '/packages', 
      icon: <FaBox className="w-7 h-7 text-amber-500 group-hover:text-amber-400 transition-colors" />,
      dotGradient: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
      dotShadow: 'rgba(245, 158, 11, 0.6)'
    },
    { 
      label: 'Blog', 
      path: '/events', 
      icon: <FaBlog className="w-7 h-7 text-cyan-500 group-hover:text-cyan-400 transition-colors" />,
      dotGradient: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)',
      dotShadow: 'rgba(6, 182, 212, 0.6)'
    },
    { 
      label: 'Brochures', 
      path: '/brochures', 
      icon: <FaFilePdf className="w-7 h-7 text-red-500 group-hover:text-red-400 transition-colors" />,
      dotGradient: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
      dotShadow: 'rgba(239, 68, 68, 0.6)'
    },
    { 
      label: 'Inquiry', 
      path: '/inquiry', 
      icon: <FaQuestionCircle className="w-7 h-7 text-indigo-500 group-hover:text-indigo-400 transition-colors" />,
      dotGradient: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
      dotShadow: 'rgba(99, 102, 241, 0.6)'
    },
    { 
      label: 'Contact Us', 
      path: '/contact', 
      icon: <FaEnvelope className="w-7 h-7 text-sky-500 group-hover:text-sky-400 transition-colors" />,
      dotGradient: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
      dotShadow: 'rgba(14, 165, 233, 0.6)'
    },
  ] as const;

  const toggleDropdown = (key: string, event: React.MouseEvent) => {
    event.stopPropagation();
    setDropdownOpen(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const closeAllMenus = () => {
    setIsMenuOpen(false);
    setDropdownOpen({});
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        closeAllMenus();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const renderDesktopNav = () => (
    <nav ref={menuRef} className="hidden lg:flex lg:items-center lg:justify-center lg:flex-1 px-8">
      <ul className="flex items-center space-x-1">
        {navItems.map((item) => (
          <li key={item.path} className="relative group">
            {item.children ? (
              <div className="relative">
                <button
                  onClick={(e) => toggleDropdown(item.label.toLowerCase(), e)}
                  className="group flex items-center space-x-1.5 rounded-lg px-4 py-3 text-sm font-medium text-white/90 hover:text-white transition-all duration-200 hover:bg-gradient-to-r from-blue-500/10 to-cyan-500/10"
                >
                  <span className="relative group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-blue-400 group-hover:to-cyan-400">
                    {item.label}
                  </span>
                  <FaChevronDown
                    className={`h-3 w-3 transform transition-transform duration-200 ${
                      dropdownOpen[item.label.toLowerCase()] ? 'rotate-180' : ''
                    }`}
                  />
                </button>
                <AnimatePresence>
                  {dropdownOpen[item.label.toLowerCase()] && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 10 }}
                      className="absolute left-0 z-50 min-w-[220px] origin-top-left"
                    >
                      <div className="mt-2 rounded-xl bg-gray-900/95 backdrop-blur-lg border border-gray-800/50 shadow-2xl overflow-hidden">
                        <div className="p-1.5">
                          {item.children.map((child) => (
                            <Link
                              key={child.path}
                              to={child.path}
                              className="flex items-center gap-2 w-full px-4 py-2.5 text-sm text-gray-300 hover:bg-white/5 hover:text-white rounded-lg transition-colors duration-200"
                              onClick={closeAllMenus}
                            >
                              <span className="w-1 h-1 rounded-full bg-blue-400 opacity-0 group-hover:opacity-100 transition-opacity"></span>
                              <span>{child.label}</span>
                            </Link>
                          ))}
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ) : (
              <Link
                to={item.path}
                className="block rounded-lg px-4 py-3 text-sm font-medium text-white/90 hover:text-white transition-all duration-200 hover:bg-gradient-to-r from-blue-500/10 to-cyan-500/10"
              >
                <span className="relative group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-blue-400 group-hover:to-cyan-400">
                  {item.label}
                </span>
              </Link>
            )}
          </li>
        ))}
      </ul>
    </nav>
  );

  const renderMobileMenu = () => (
    <AnimatePresence>
      {isMenuOpen && (
        <div className="lg:hidden fixed inset-0 z-50 h-screen w-screen overflow-hidden">
          <motion.div 
            className="absolute inset-0 bg-black/70 backdrop-blur-xl backdropFilter: 'blur(16px)'"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={closeAllMenus}
          />
          <motion.div 
            className="absolute top-0 right-0 h-[90vh] w-full bg-gray-900/95 backdrop-blur-xl shadow-2xl flex flex-col rounded-b-3xl overflow-hidden"
            style={{
              backdropFilter: 'blur(16px)',
              WebkitBackdropFilter: 'blur(16px)'
            }}
            initial={{ y: '-100%' }}
            animate={{ y: 0 }}
            exit={{ y: '-100%' }}
            transition={{ 
              type: 'tween', 
              ease: [0.16, 1, 0.3, 1], 
              duration: 0.4 
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header with Logo */}
            <div className="px-6 py-4 border-b border-gray-800">
              <div className="flex items-center justify-between">
                <Link to="/" className="flex items-center" onClick={closeAllMenus}>
                  <img
                    src="/images/logo.png"
                    alt="SS Solar Logo"
                    className="h-10 w-auto object-contain"
                  />
                </Link>
                <button
                  onClick={closeAllMenus}
                  className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-full transition-colors"
                >
                  <FaTimes size={24} />
                </button>
              </div>
            </div>

            {/* Navigation Items */}
            <nav className="flex-1 overflow-y-auto py-3 px-6">
              <div className="space-y-0.5">
                {mobileNavItems.map((item, index) => (
                  <motion.div
                    key={item.path}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{
                      delay: index * 0.05,
                      type: 'spring',
                      stiffness: 100,
                      damping: 10
                    }}
                  >
                    <Link
                      to={item.path}
                      className="group flex items-center px-5 pr-6 py-4 text-lg font-medium text-white hover:bg-gray-800/50 rounded-lg transition-all duration-300 transform hover:translate-x-1"
                      onClick={closeAllMenus}
                    >
                      <motion.span 
                        className="w-4 h-4 rounded-full mr-6"
                        style={{
                          background: item.dotGradient,
                          boxShadow: `0 0 8px ${item.dotShadow}`
                        }}
                        initial={{ opacity: 0, scale: 0.5 }}
                        animate={{ 
                          opacity: [0.3, 1, 0.7, 0.3],
                          scale: [0.7, 1.3, 0.9, 0.7],
                          boxShadow: [
                            `0 0 8px ${item.dotShadow}`,
                            `0 0 12px ${item.dotShadow.replace('0.6', '0.8')}`,
                            `0 0 8px ${item.dotShadow}`,
                            `0 0 4px ${item.dotShadow.replace('0.6', '0.4')}`
                          ]
                        }}
                        transition={{
                          duration: 2,
                          repeat: Infinity,
                          repeatType: 'loop',
                          ease: 'easeInOut'
                        }}
                      />
                      <span className="mr-4">
                        {item.icon}
                      </span>
                      <span className="flex-1 text-lg">{item.label}</span>
                    </Link>
                  </motion.div>
                ))}
              </div>
            </nav>

            {/* Contact Buttons */}
            <div className="p-4 border-t border-gray-800">
              <div className="grid grid-cols-2 gap-3">
                <a
                  href="tel:+919887710260"
                  className="flex items-center justify-center rounded-lg bg-blue-600 px-4 py-3 text-sm font-medium text-white hover:bg-blue-700 transition-colors"
                  onClick={closeAllMenus}
                >
                  <span className="mr-2">📞</span>
                  Call Now
                </a>
                <button
                  onClick={() => {
                    setWhatsappPopupOpen(true);
                    closeAllMenus();
                  }}
                  className="flex items-center justify-center rounded-lg bg-green-600 px-4 py-3 text-sm font-medium text-white hover:bg-green-700 transition-colors"
                >
                  <span className="mr-2">💬</span>
                  WhatsApp
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );

  return (
    <header className="fixed left-0 top-0 z-50 w-full bg-black/40 backdrop-blur-lg border-b border-white/10 shadow-xl [@supports(backdrop-filter:blur(0))]:bg-black/30">
      <div className="max-w-7xl mx-auto px-4 lg:px-8 py-2">
        <div className="relative flex items-center justify-between min-h-[56px]">
          {/* Logo */}
          <div className="flex-shrink-0">
            <Link to="/" className="flex items-center gap-2">
              <img
                src="/images/logo.png"
                alt="SS Solar Logo"
                className="h-10 w-auto object-contain"
              />
              <div className="hidden sm:flex flex-col">
                <h1 className="text-lg font-bold text-white tracking-wide font-sans uppercase">SS Solar</h1>
                <p className="text-[10px] font-light tracking-wider text-white/80">
                  Innovation Power Exploring Energy
                </p>
              </div>
            </Link>
          </div>

          {/* Desktop Navigation */}
          {renderDesktopNav()}

          {/* Desktop Auth Buttons */}
          <div className="hidden lg:flex lg:items-center lg:space-x-3">
            <a
              href="tel:+919887710260"
              className="flex items-center justify-center rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 transition-colors"
            >
              <span className="mr-1.5">📞</span>
              Call Now
            </a>
            <button
              onClick={() => setWhatsappPopupOpen(true)}
              className="flex items-center justify-center rounded-md bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 transition-colors"
            >
              <span className="mr-1.5">💬</span>
              WhatsApp
            </button>
          </div>

          {/* Mobile Menu Button */}
          <div className="flex items-center lg:hidden">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="inline-flex items-center justify-center p-3 rounded-md text-white hover:bg-white/10 focus:outline-none lg:hidden"
            >
              {isMenuOpen ? <FaTimes size={20} /> : <FaBars className="h-8 w-8" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation Menu */}
      {renderMobileMenu()}

      {/* WhatsApp Popup */}
      <WhatsAppPopup 
        isOpen={whatsappPopupOpen} 
        onClose={() => setWhatsappPopupOpen(false)} 
      />
    </header>
  );
};

export default Header;